import { Person } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Link as MUILink,
  TextField,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { getBaseHeaders, platformApiBaseUrl, useOystehr } from '../../components';
import { AdminSidebarItem } from '../../components/AdminSidebar';
import { ConfirmationDialog } from '../../components/ConfirmationDialog';
import { PageContainer } from '../../components/PageContainer';
import { TextCardWithButton } from '../../components/TextCardWithButton';
import { TextFieldWithCopyButton } from '../../components/TextFieldWithCopyButton';
import { copyToClipboard } from '../../lib/utils';

export const profileSidebarItems: AdminSidebarItem[][] = [
  [
    {
      label: 'Profile',
      icon: <Person />,
      path: '/profile',
    },
  ],
  [],
];

export function ProfilePage(): JSX.Element {
  const { getAuthToken, developerM2MClientId, refetchUserMeDeveloperM2MClientId } = useOystehr();

  const [actionLoading, setActionLoading] = useState(false);
  const [secretValue, setSecretValue] = useState<string | null>(null);
  const [showRotateSecretDialog, setShowRotateSecretDialog] = useState(false);
  const [showUpdatedSecretDialog, setShowUpdatedSecretDialog] = useState(false);

  const rotateSecretButtonHandler = (): void => {
    setShowRotateSecretDialog(true);
  };

  const handleCloseRotateSecretDialog = (): void => {
    setShowRotateSecretDialog(false);
  };

  const copySecretButtonHandler = (): void => {
    if (secretValue) {
      copyToClipboard(secretValue, 'Secret key copied to clipboard!');
    } else {
      toast.error('An error occurred copying the secret key, please try again or contact us');
    }
  };

  const rotateSecret = React.useCallback((): void => {
    if (actionLoading) {
      return;
    }
    const doRotate = async (): Promise<void> => {
      setActionLoading(true);
      const token = await getAuthToken();
      const response = await fetch(`${platformApiBaseUrl}/m2m/rotate-secret`, {
        method: 'POST',
        headers: getBaseHeaders(token),
        credentials: 'same-origin',
      });

      if (!response.ok) {
        toast.error(`An error occurred: ${response.body || 'Unknown error'}`);
      } else {
        const json = await response.json();
        setSecretValue(json.secret ?? null);
        setShowUpdatedSecretDialog(true);
        toast.success('Successfully rotated client secret');
      }

      await refetchUserMeDeveloperM2MClientId();

      setActionLoading(false);

      handleCloseRotateSecretDialog();
    };
    void doRotate();
  }, [actionLoading, getAuthToken, refetchUserMeDeveloperM2MClientId]);

  return (
    <PageContainer sidebarItems={profileSidebarItems}>
      <div>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              marginBottom: 2,
            }}
          >
            <Typography variant="h4">Profile</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextFieldWithCopyButton
              textField={
                <TextField
                  fullWidth
                  value={developerM2MClientId ?? 'Client ID will be generated when you first rotate the secret'}
                  label="Developer M2M Client ID"
                  helperText="cannot be edited"
                  disabled={true}
                />
              }
              copyButtonOnClick={() =>
                copyToClipboard(developerM2MClientId ?? '', 'Developer M2M Client ID copied to clipboard!')
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextCardWithButton
              title="Rotate Developer M2M Client Secret"
              description="Once you generate a new one, you will not be able to access it again from this page. Store it somewhere securely."
              variant="warning"
              buttonTitle="Rotate"
              action={rotateSecretButtonHandler}
              loading={actionLoading}
            />
          </Grid>
          <Dialog
            open={showUpdatedSecretDialog}
            onClose={() => setShowUpdatedSecretDialog(false)}
            fullWidth
            maxWidth={'md'}
          >
            <DialogTitle>Your Developer M2M Secret Key</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Use your Developer M2M Client ID / Secret pair to&nbsp;
                <MUILink
                  target="_blank"
                  href="https://docs.oystehr.com/oystehr/core-documentation/authenticating-api-requests/#getting-an-access-token"
                >
                  get an access token to securely call the API.
                </MUILink>
              </DialogContentText>
              <TextFieldWithCopyButton
                textField={
                  <TextField
                    variant="standard"
                    fullWidth
                    value={secretValue}
                    sx={{ mt: '20px' }}
                    helperText="This is your new secret key. Store it securely."
                    disabled={true}
                  />
                }
                copyButtonOnClick={copySecretButtonHandler}
              />
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => {
                  setShowUpdatedSecretDialog(false);
                }}
              >
                Ok
              </Button>
            </DialogActions>
          </Dialog>
          {showRotateSecretDialog && (
            <ConfirmationDialog
              handleAction={rotateSecret}
              open={showRotateSecretDialog}
              handleClose={handleCloseRotateSecretDialog}
            />
          )}
        </Grid>
      </div>
    </PageContainer>
  );
}
